"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useAuth } from "@/hooks/use-auth"
import {
  agentDeliverySettingsQueryKey,
  tenantDeliveryDefaultsQueryKey,
} from "@/hooks/queries/delivery-bindings"
import { getDeliveryQueryTenantId } from "@/hooks/queries/delivery-integrations"
import {
  agentIntegrationStatusesQueryKey,
  agentIntegrationStatusesTenantQueryKey,
} from "@/hooks/queries/agent-integration-status-keys"
import { withQueryHandling } from "@/lib/with-query-handling"
import {
  clearTenantDeliveryDefaultsAction,
  saveAgentCrmStatusFallbackAction,
  type SaveCrmStatusFallbackInput,
  saveAgentDeliverySettingsAction,
  saveTenantDeliveryDefaultsAction,
  type AgentDeliverySettingsView,
  type SaveAgentDeliverySettingsInput,
  type SaveTenantDeliveryDefaultsInput,
  type TenantDeliveryDefaultsView,
} from "@/server-actions/delivery-bindings"

type FormErrorSetter = (errors: Record<string, unknown>) => void

export function useSaveCrmStatusFallbackMutation() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const tenantId = getDeliveryQueryTenantId(user)
  return useMutation({
    mutationFn: async (input: SaveCrmStatusFallbackInput & { setErrors?: FormErrorSetter }) => {
      const { setErrors, ...values } = input
      const result = await withQueryHandling(saveAgentCrmStatusFallbackAction(values), {
        loading: "Saving CRM status fallback…", success: "CRM status fallback saved", setErrors,
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: value => {
      queryClient.setQueryData(agentDeliverySettingsQueryKey(tenantId, value.scopeType, value.scopeId), value)
    },
  })
}

export function useSaveTenantDeliveryDefaultsMutation() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const tenantId = getDeliveryQueryTenantId(user)
  const queryKey = tenantDeliveryDefaultsQueryKey(tenantId)

  return useMutation({
    mutationFn: async (input: SaveTenantDeliveryDefaultsInput & {
      setErrors?: FormErrorSetter
    }): Promise<TenantDeliveryDefaultsView> => {
      const { setErrors, ...actionInput } = input
      const result = await withQueryHandling(
        saveTenantDeliveryDefaultsAction(actionInput),
        {
          loading: "Saving delivery defaults...",
          success: "Delivery defaults saved for all agents",
          setErrors,
        },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async value => {
      queryClient.setQueryData(queryKey, value)
      await queryClient.invalidateQueries({
        queryKey: agentIntegrationStatusesTenantQueryKey(tenantId),
      })
    },
  })
}

export function useClearTenantDeliveryDefaultsMutation() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const tenantId = getDeliveryQueryTenantId(user)
  const queryKey = tenantDeliveryDefaultsQueryKey(tenantId)

  return useMutation({
    mutationFn: async (): Promise<TenantDeliveryDefaultsView> => {
      const result = await withQueryHandling(
        clearTenantDeliveryDefaultsAction({}),
        {
          loading: "Clearing delivery defaults...",
          success: "Delivery defaults cleared",
        },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async value => {
      queryClient.setQueryData(queryKey, value)
      await queryClient.invalidateQueries({
        queryKey: agentIntegrationStatusesTenantQueryKey(tenantId),
      })
    },
  })
}

export function useSaveAgentDeliverySettingsMutation() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const tenantId = getDeliveryQueryTenantId(user)

  return useMutation({
    mutationFn: async (input: SaveAgentDeliverySettingsInput & {
      setErrors?: FormErrorSetter
    }): Promise<AgentDeliverySettingsView> => {
      const { setErrors, ...actionInput } = input
      const result = await withQueryHandling(
        saveAgentDeliverySettingsAction(actionInput),
        {
          loading: "Saving delivery settings...",
          success: "Delivery settings saved",
          setErrors,
        },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async value => {
      queryClient.setQueryData(
        agentDeliverySettingsQueryKey(
          tenantId,
          value.scopeType,
          value.scopeId,
        ),
        value,
      )
      await queryClient.invalidateQueries({
        queryKey: agentIntegrationStatusesQueryKey(
          tenantId,
          value.scopeType,
        ),
      })
    },
  })
}
